import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Chip,
  Grid,
  Rating,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useForm, Controller } from 'react-hook-form';
import { leadsApi, Lead, CreateLeadData, UpdateLeadData, LeadFilters } from '../services/leads';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';
import { CURRENCIES } from '../utils/currency';
import { useCompany } from '../contexts/CompanyContext';

const LEAD_SOURCES = [
  'Website',
  'Referral',
  'Social Media',
  'Cold Call',
  'Email Campaign',
  'Trade Show',
  'Other',
];

const Leads: React.FC = () => {
  const { baseCurrency } = useCompany();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [viewingLead, setViewingLead] = useState<Lead | null>(null);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  const leadForm = useForm<CreateLeadData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      jobTitle: '',
      status: 'new',
      source: '',
      priority: 'medium',
      rating: 0,
      estimatedValue: undefined,
      currency: baseCurrency,
      notes: '',
      nextFollowUp: '',
    },
  });

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: LeadFilters = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter as 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
      }
      if (priorityFilter !== 'all') {
        filters.priority = priorityFilter as 'low' | 'medium' | 'high';
      }
      if (sourceFilter !== 'all') {
        filters.source = sourceFilter;
      }
      
      const data = await leadsApi.getAll(filters);
      setLeads(data || []);
    } catch (err: any) {
      console.error('Error loading leads:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, sourceFilter]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleOpenCreateDialog = () => {
    leadForm.reset({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      jobTitle: '',
      status: 'new',
      source: '',
      priority: 'medium',
      rating: 0,
      estimatedValue: undefined,
      currency: baseCurrency,
      notes: '',
      nextFollowUp: '',
    });
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    leadForm.reset();
  };

  const handleOpenEditDialog = (lead: Lead) => {
    setEditingLead(lead);
    leadForm.reset({
      firstName: lead.firstName,
      lastName: lead.lastName,
      email: lead.email || '',
      phone: lead.phone || '',
      companyName: lead.companyName || '',
      jobTitle: lead.jobTitle || '',
      status: lead.status,
      source: lead.source || '',
      priority: lead.priority,
      rating: lead.rating,
      estimatedValue: lead.estimatedValue,
      currency: lead.currency,
      notes: lead.notes || '',
      nextFollowUp: lead.nextFollowUp ? format(parseISO(lead.nextFollowUp), 'yyyy-MM-dd') : '',
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingLead(null);
    leadForm.reset();
  };

  const handleOpenViewDialog = (lead: Lead) => {
    setViewingLead(lead);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingLead(null);
  };

  const handleOpenDeleteDialog = (lead: Lead) => {
    setLeadToDelete(lead);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setLeadToDelete(null);
  };

  const handleCreateLead = async (data: CreateLeadData) => {
    try {
      setError(null);
      await leadsApi.create(data);
      handleCloseCreateDialog();
      loadLeads();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create lead');
    }
  };

  const handleUpdateLead = async (data: UpdateLeadData) => {
    if (!editingLead) return;
    try {
      setError(null);
      await leadsApi.update(editingLead.id, data);
      handleCloseEditDialog();
      loadLeads();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update lead');
    }
  };

  const handleDeleteLead = async () => {
    if (!leadToDelete) return;
    try {
      setError(null);
      await leadsApi.delete(leadToDelete.id);
      handleCloseDeleteDialog();
      loadLeads();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete lead');
    }
  };

  const handleConvertToClient = async (lead: Lead) => {
    // This would typically open a dialog to create a client from the lead
    // For now, just update the status
    try {
      setError(null);
      await leadsApi.update(lead.id, { status: 'converted', convertedDate: format(new Date(), 'yyyy-MM-dd') });
      loadLeads();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to convert lead');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'default';
      case 'contacted': return 'info';
      case 'qualified': return 'primary';
      case 'converted': return 'success';
      case 'lost': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getTotalEstimatedValue = () => {
    return leads
      .filter(lead => lead.status !== 'lost' && lead.status !== 'converted' && lead.estimatedValue)
      .reduce((sum, lead) => sum + (lead.estimatedValue || 0), 0);
  };

  if (loading && leads.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">CRM - Leads</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Add Lead
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {leads.filter(l => l.status === 'new').length}
            </Typography>
            <Typography variant="body2" color="textSecondary">New Leads</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="info.main">
              {leads.filter(l => l.status === 'qualified').length}
            </Typography>
            <Typography variant="body2" color="textSecondary">Qualified</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="success.main">
              {leads.filter(l => l.status === 'converted').length}
            </Typography>
            <Typography variant="body2" color="textSecondary">Converted</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {formatCurrency(getTotalEstimatedValue(), 'USD')}
            </Typography>
            <Typography variant="body2" color="textSecondary">Pipeline Value</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="qualified">Qualified</MenuItem>
                <MenuItem value="converted">Converted</MenuItem>
                <MenuItem value="lost">Lost</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                label="Priority"
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <MenuItem value="all">All Sources</MenuItem>
                {LEAD_SOURCES.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              onClick={() => {
                setStatusFilter('all');
                setPriorityFilter('all');
                setSourceFilter('all');
              }}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Leads Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell align="right">Est. Value</TableCell>
                <TableCell>Next Follow-up</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography color="textSecondary">No leads found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {lead.firstName} {lead.lastName}
                      </Typography>
                      {lead.jobTitle && (
                        <Typography variant="caption" color="textSecondary">
                          {lead.jobTitle}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{lead.companyName || '-'}</TableCell>
                    <TableCell>{lead.email || '-'}</TableCell>
                    <TableCell>{lead.phone || '-'}</TableCell>
                    <TableCell>{lead.source || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                        size="small"
                        color={getStatusColor(lead.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.priority.charAt(0).toUpperCase() + lead.priority.slice(1)}
                        size="small"
                        color={getPriorityColor(lead.priority) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Rating value={lead.rating} readOnly size="small" />
                    </TableCell>
                    <TableCell align="right">
                      {lead.estimatedValue ? formatCurrency(lead.estimatedValue, lead.currency) : '-'}
                    </TableCell>
                    <TableCell>
                      {lead.nextFollowUp ? format(parseISO(lead.nextFollowUp), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenViewDialog(lead)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {lead.status !== 'converted' && lead.status !== 'lost' && (
                          <Tooltip title="Convert to Client">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleConvertToClient(lead)}
                            >
                              <PersonAddIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(lead)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(lead)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Lead Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <form onSubmit={leadForm.handleSubmit(handleCreateLead)}>
          <DialogTitle>Add Lead</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    required
                    {...leadForm.register('firstName', { required: 'First name is required' })}
                    error={!!leadForm.formState.errors.firstName}
                    helperText={leadForm.formState.errors.firstName?.message}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    required
                    {...leadForm.register('lastName', { required: 'Last name is required' })}
                    error={!!leadForm.formState.errors.lastName}
                    helperText={leadForm.formState.errors.lastName?.message}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    {...leadForm.register('email')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    {...leadForm.register('phone')}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    {...leadForm.register('companyName')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    {...leadForm.register('jobTitle')}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Controller
                    name="status"
                    control={leadForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status">
                          <MenuItem value="new">New</MenuItem>
                          <MenuItem value="contacted">Contacted</MenuItem>
                          <MenuItem value="qualified">Qualified</MenuItem>
                          <MenuItem value="converted">Converted</MenuItem>
                          <MenuItem value="lost">Lost</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="source"
                    control={leadForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Source</InputLabel>
                        <Select {...field} label="Source">
                          <MenuItem value="">None</MenuItem>
                          {LEAD_SOURCES.map((source) => (
                            <MenuItem key={source} value={source}>
                              {source}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="priority"
                    control={leadForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select {...field} label="Priority">
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Controller
                    name="rating"
                    control={leadForm.control}
                    render={({ field }) => (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>Rating</Typography>
                        <Rating
                          value={field.value}
                          onChange={(event, newValue) => {
                            field.onChange(newValue || 0);
                          }}
                        />
                      </Box>
                    )}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Est. Value"
                    type="number"
                    inputProps={{ step: 0.01, min: 0 }}
                    {...leadForm.register('estimatedValue', { valueAsNumber: true })}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Controller
                    name="currency"
                    control={leadForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency">
                          {CURRENCIES.map((currency) => (
                            <MenuItem key={currency.code} value={currency.code}>
                              {currency.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Next Follow-up"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...leadForm.register('nextFollowUp')}
              />
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                {...leadForm.register('notes')}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Create Lead
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <form onSubmit={leadForm.handleSubmit(handleUpdateLead)}>
          <DialogTitle>Edit Lead</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    required
                    {...leadForm.register('firstName', { required: 'First name is required' })}
                    error={!!leadForm.formState.errors.firstName}
                    helperText={leadForm.formState.errors.firstName?.message}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    required
                    {...leadForm.register('lastName', { required: 'Last name is required' })}
                    error={!!leadForm.formState.errors.lastName}
                    helperText={leadForm.formState.errors.lastName?.message}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    {...leadForm.register('email')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    {...leadForm.register('phone')}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    {...leadForm.register('companyName')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    {...leadForm.register('jobTitle')}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Controller
                    name="status"
                    control={leadForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status">
                          <MenuItem value="new">New</MenuItem>
                          <MenuItem value="contacted">Contacted</MenuItem>
                          <MenuItem value="qualified">Qualified</MenuItem>
                          <MenuItem value="converted">Converted</MenuItem>
                          <MenuItem value="lost">Lost</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="source"
                    control={leadForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Source</InputLabel>
                        <Select {...field} label="Source">
                          <MenuItem value="">None</MenuItem>
                          {LEAD_SOURCES.map((source) => (
                            <MenuItem key={source} value={source}>
                              {source}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="priority"
                    control={leadForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select {...field} label="Priority">
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Controller
                    name="rating"
                    control={leadForm.control}
                    render={({ field }) => (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>Rating</Typography>
                        <Rating
                          value={field.value}
                          onChange={(event, newValue) => {
                            field.onChange(newValue || 0);
                          }}
                        />
                      </Box>
                    )}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Est. Value"
                    type="number"
                    inputProps={{ step: 0.01, min: 0 }}
                    {...leadForm.register('estimatedValue', { valueAsNumber: true })}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Controller
                    name="currency"
                    control={leadForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency">
                          {CURRENCIES.map((currency) => (
                            <MenuItem key={currency.code} value={currency.code}>
                              {currency.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Next Follow-up"
                type="date"
                InputLabelProps={{ shrink: true }}
                {...leadForm.register('nextFollowUp')}
              />
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                {...leadForm.register('notes')}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Update Lead
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Lead Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Lead Details</DialogTitle>
        <DialogContent>
          {viewingLead && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    {viewingLead.firstName} {viewingLead.lastName}
                  </Typography>
                  {viewingLead.jobTitle && (
                    <Typography variant="body2" color="textSecondary">
                      {viewingLead.jobTitle}
                    </Typography>
                  )}
                </Grid>
                {viewingLead.companyName && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Company</Typography>
                    <Typography variant="body1">{viewingLead.companyName}</Typography>
                  </Grid>
                )}
                {viewingLead.email && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{viewingLead.email}</Typography>
                  </Grid>
                )}
                {viewingLead.phone && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                    <Typography variant="body1">{viewingLead.phone}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip
                    label={viewingLead.status.charAt(0).toUpperCase() + viewingLead.status.slice(1)}
                    size="small"
                    color={getStatusColor(viewingLead.status) as any}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Priority</Typography>
                  <Chip
                    label={viewingLead.priority.charAt(0).toUpperCase() + viewingLead.priority.slice(1)}
                    size="small"
                    color={getPriorityColor(viewingLead.priority) as any}
                  />
                </Grid>
                {viewingLead.source && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Source</Typography>
                    <Typography variant="body1">{viewingLead.source}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Rating</Typography>
                  <Rating value={viewingLead.rating} readOnly size="small" />
                </Grid>
                {viewingLead.estimatedValue && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Estimated Value</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(viewingLead.estimatedValue, viewingLead.currency)}
                    </Typography>
                  </Grid>
                )}
                {viewingLead.nextFollowUp && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Next Follow-up</Typography>
                    <Typography variant="body1">
                      {format(parseISO(viewingLead.nextFollowUp), 'MMM d, yyyy')}
                    </Typography>
                  </Grid>
                )}
                {viewingLead.convertedDate && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Converted Date</Typography>
                    <Typography variant="body1">
                      {format(parseISO(viewingLead.convertedDate), 'MMM d, yyyy')}
                    </Typography>
                  </Grid>
                )}
                {viewingLead.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                    <Typography variant="body1">{viewingLead.notes}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Created: {format(parseISO(viewingLead.createdAt), 'MMM d, yyyy HH:mm')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Lead Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Lead</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this lead?
            <br />
            <br />
            Name: {leadToDelete?.firstName} {leadToDelete?.lastName}
            {leadToDelete?.companyName && (
              <>
                <br />
                Company: {leadToDelete.companyName}
              </>
            )}
            <br />
            <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteLead} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Leads;
